import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import { useStyles } from './styles/rentEditStyles';

import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Tab from '@material-ui/core/Tab';

import Tabs from 'components/CustomTabs/CustomTabs';
import ControlButtons from 'components/ControlButtons';
import DialogHeader from 'components/DialogHeader/DialogHeader';
import compose from 'helpers/compose';
import LoadingIndicator from 'components/Loading/LoadingIndicator';

import { TargetOptions } from 'stores/workflowStores/finances/targets/model';
import { RentModalsStore, rentFlow, Component } from 'stores/workflowStores/finances/rentModalsStore';

import { CalculationMode } from 'common/interfaces';
import RentTypeSelection from './RentTypeSelection';
import CurrentIncome from './CurrentIncome';
import Retirement from './Retirement';
import Rent from './Rent';
import Insurance from './Insurance';
import PlanningFor from './PlanningFor';
import { RootStore } from 'stores/rootStore';
import { PersonType, AccountMode } from 'enums';
import { AccountBasic } from 'stores/workflowStores/workflowBasicStore/model';

interface OwnProps {
  opened: boolean;
  handleClose: () => void;
  defaultInflation: number;
  defaultInflationType: string;
  mode: AccountMode;
  options: TargetOptions;
  accountId: string;
  existingFlow?: CalculationMode;
  rootStore: RootStore;
  fixedHeight?: boolean;
  personType: PersonType;

  plan?: boolean;
  detailMode?: boolean;
  planBasicData?: AccountBasic;
  rentApplied?: boolean;
  refresh: () => void;
}

const RentModalComplete: React.FC<OwnProps> = ({
  opened,
  handleClose,
  defaultInflation,
  defaultInflationType,
  mode,
  options,
  existingFlow,
  accountId,
  rootStore,
  fixedHeight,
  plan,
  detailMode,
  personType,
  planBasicData,
  refresh,
}) => {
  const [store] = useState(new RentModalsStore(handleClose, existingFlow));
  const { currency } = planBasicData || rootStore.stores.editAccountStore;
  const basicData = planBasicData || rootStore.stores.editAccountStore.data;
  const { t } = useTranslation();
  const classes = useStyles();
  const {
    rentSubmitting,
    onBackRentTab,
    rentNextText,
    rentBackText,
    rentFormKey,
    flow,
    flowTab,
    handleRentTabChange,
    component,
    partner,
  } = store;

  useEffect(() => {
    if (!existingFlow) store.setFlow(CalculationMode.NONE);
  }, [store, existingFlow]);

  return (
    <Dialog open={opened} aria-labelledby="form-dialog-title">
      <DialogHeader onClose={handleClose}>
        {flow !== CalculationMode.NONE && (
          <>
            {t('finQuestionnary_rentModalTitle')} — {t(`calculationMode_rent_${flow}`)}{' '}
          </>
        )}
      </DialogHeader>

      {flow === CalculationMode.NONE ? (
        <RentTypeSelection store={store} personType={personType} />
      ) : (
        <>
          <DialogContent>
            {rentFlow[flow].length > 1 && (
              <Tabs value={flowTab} onChange={handleRentTabChange} fullWidth={true}>
                {rentFlow[flow].map((step, i) => (
                  <Tab label={t(step.title)} key={i} id={`rent-modal-${flowTab}-tab`} />
                ))}
              </Tabs>
            )}
            <div
              className={flow === CalculationMode.REQUIRED ? classes.fixedHeight : ''}
              style={{ position: 'relative' }}
            >
              {store.loading && (
                <div
                  style={{
                    position: 'absolute',
                    width: '100%',
                    height: '8em',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <LoadingIndicator size={56} />
                </div>
              )}

              {component === Component.PLANNING_FOR && (
                <PlanningFor accountId={accountId} classes={classes} rentModalsStore={store} detailMode={detailMode} />
              )}
              {component === Component.CURRENT_INCOME && (
                <CurrentIncome
                  rentModalsStore={store}
                  accountId={accountId}
                  partner={partner}
                  classes={classes}
                  basicData={basicData}
                  currency={currency}
                  detailMode={detailMode}
                />
              )}
              {component === Component.INSURANCE && (
                <Insurance
                  rentModalsStore={store}
                  accountId={accountId}
                  currency={currency}
                  partner={partner}
                  classes={classes}
                  basicData={basicData}
                  detailMode={detailMode}
                />
              )}
              {component === Component.RETIREMENT && (
                <Retirement
                  rentModalsStore={store}
                  accountId={accountId}
                  partner={partner}
                  classes={classes}
                  basicData={basicData}
                  currency={currency}
                  detailMode={detailMode}
                />
              )}
              {component === Component.RENT && (
                <Rent
                  flow={flow}
                  resetFlow={store.resetFlow}
                  mode={mode}
                  rentModalsStore={store}
                  accountId={accountId}
                  classes={classes}
                  currency={currency}
                  //FIXME: only options?
                  defaultInflation={defaultInflation}
                  defaultInflationType={defaultInflationType}
                  options={options}
                  plan={plan}
                  detailMode={detailMode}
                  personType={personType}
                  reset={!existingFlow || existingFlow !== flow}
                  refresh={refresh}
                />
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <ControlButtons
              isSubmitting={rentSubmitting}
              onBack={onBackRentTab}
              nextText={t(rentNextText)}
              backText={t(rentBackText)}
              form={rentFormKey}
              nextButtonId="rent-modal-next-button"
              backButtonId="rent-modal-back-button"
            />
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default compose(inject('rootStore'), observer)(RentModalComplete);
